import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, LinearProgress, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDateBR } from '../../utils/date';
import { ExecutionTask, TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, TaskPriority, TaskStatus } from '../../types/execution';

interface TaskProject { id: number; name: string; workstreams: { id: number; name: string; activities: { id: number; name: string; depth: number }[] }[] }
const EMPTY_FORM = { project_id: '', workstream_id: '', activity_id: '', title: '', description: '', due_date: '', priority: 'MEDIUM' as TaskPriority };

type MyTask = ExecutionTask & { project_name: string };
const STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'DONE'];
const today = () => format(new Date(), 'yyyy-MM-dd');

/** Tarefas de projeto atribuídas à pessoa logada: ela atualiza o status e registra comentários. */
const MyTasksSection: React.FC = () => {
    const [tasks, setTasks] = useState<MyTask[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hideDone, setHideDone] = useState(true);
    const [commenting, setCommenting] = useState<MyTask | null>(null);
    const [comment, setComment] = useState('');
    const { user } = useAuth();
    const [projects, setProjects] = useState<TaskProject[]>([]);
    const [editing, setEditing] = useState<{ task: MyTask | null } | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setTasks((await api.get('/api/collaborator/project-tasks')).data);
            setError(null);
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao carregar suas tarefas.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('/api/collaborator/project-tasks/projects').then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }, []);

    // Tarefas que a própria pessoa criou: edição completa e exclusão
    const ownCreated = (t: MyTask) => t.created_by === user?.id;

    const openForm = (task: MyTask | null) => {
        setForm(task ? {
            project_id: String(task.project_id), workstream_id: task.workstream_id ? String(task.workstream_id) : '',
            activity_id: task.activity_id ? String(task.activity_id) : '',
            title: task.title, description: task.description || '', due_date: task.due_date || '', priority: task.priority,
        } : { ...EMPTY_FORM, project_id: projects.length === 1 ? String(projects[0].id) : '' });
        setEditing({ task });
    };

    const saveForm = async () => {
        if (!editing) return;
        const payload = {
            title: form.title, description: form.description || null, due_date: form.due_date || null,
            priority: form.priority, workstream_id: form.workstream_id ? Number(form.workstream_id) : null,
            activity_id: form.activity_id ? Number(form.activity_id) : null,
        };
        try {
            if (editing.task) await api.put(`/api/collaborator/project-tasks/${editing.task.id}`, payload);
            else await api.post('/api/collaborator/project-tasks', { ...payload, project_id: Number(form.project_id) });
            setEditing(null);
            load();
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao salvar a tarefa.');
        }
    };

    const removeOwn = async (task: MyTask) => {
        if (!window.confirm(`Excluir a tarefa "${task.title}"?`)) return;
        try {
            await api.delete(`/api/collaborator/project-tasks/${task.id}`);
            setTasks((prev) => prev.filter((t) => t.id !== task.id));
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao excluir a tarefa.');
        }
    };

    const formProject = projects.find((p) => String(p.id) === form.project_id);

    const update = async (task: MyTask, data: { status?: TaskStatus; comment?: string }) => {
        try {
            const updated: MyTask = (await api.put(`/api/collaborator/project-tasks/${task.id}`, data)).data;
            setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
            return true;
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao atualizar a tarefa.');
            return false;
        }
    };

    const isLate = (t: MyTask) => t.status !== 'DONE' && !!t.due_date && t.due_date < today();
    const visible = useMemo(() => tasks.filter((t) => !hideDone || t.status !== 'DONE'), [tasks, hideDone]);
    const late = tasks.filter(isLate).length;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <Typography variant="h6">Minhas tarefas</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {late > 0 && <Chip color="error" label={`${late} atrasada(s)`} />}
                    <FormControlLabel label="Ocultar concluídas" control={<Switch checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />} />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm(null)} disabled={projects.length === 0}>
                        Nova tarefa
                    </Button>
                </Stack>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {!loading && visible.length === 0 && (
                <Alert severity="info">
                    {tasks.length === 0
                        ? (projects.length
                    ? 'Nenhuma tarefa ainda. Crie as suas em "Nova tarefa" ou aguarde as que o gestor atribuir a você.'
                    : 'Você ainda não participa de nenhum projeto em execução. As tarefas aparecem aqui quando o gestor atribui uma a você.')
                        : 'Nenhuma tarefa em aberto.'}
                </Alert>
            )}
            <Stack spacing={1.5}>
                {visible.map((task) => (
                    <Card key={task.id} variant="outlined" sx={{ borderLeft: isLate(task) ? '4px solid #c62828' : undefined }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                                <Box minWidth={0} flex="1 1 260px">
                                    <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{task.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {task.project_name}{task.workstream_name ? ` · ${task.workstream_name}` : ''}
                                        {task.activity_name ? ` · ${task.activity_name}` : ''}
                                    </Typography>
                                    {task.description && (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1 }}>{task.description}</Typography>
                                    )}
                                    <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" useFlexGap>
                                        <Chip size="small" variant="outlined" label={`Prioridade ${TASK_PRIORITY_LABELS[task.priority].toLowerCase()}`} />
                                        {task.due_date && (
                                            <Chip size="small" color={isLate(task) ? 'error' : 'default'}
                                                label={`${isLate(task) ? 'Venceu' : 'Prazo'} ${formatDateBR(task.due_date)}`} />
                                        )}
                                    </Stack>
                                </Box>
                                <Stack spacing={1} sx={{ minWidth: 180 }}>
                                    <TextField select size="small" label="Status" value={task.status}
                                        onChange={(e) => update(task, { status: e.target.value as TaskStatus })}>
                                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</MenuItem>)}
                                    </TextField>
                                    <Button size="small" variant="outlined" onClick={() => { setComment(''); setCommenting(task); }}>
                                        Comentar
                                    </Button>
                                    {ownCreated(task) && (
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" onClick={() => openForm(task)}>Editar</Button>
                                            <Button size="small" color="error" onClick={() => removeOwn(task)}>Excluir</Button>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.task ? 'Editar tarefa' : 'Nova tarefa para mim'}</DialogTitle>
                <DialogContent>
                    <TextField select fullWidth margin="normal" label="Projeto" value={form.project_id} disabled={!!editing?.task}
                        onChange={(e) => setForm({ ...form, project_id: e.target.value, workstream_id: '' })}>
                        {projects.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth margin="normal" label="Frente (opcional)" value={form.workstream_id}
                        disabled={!formProject} onChange={(e) => setForm({ ...form, workstream_id: e.target.value, activity_id: '' })}>
                        <MenuItem value="">Sem frente</MenuItem>
                        {(formProject?.workstreams || []).map((w) => <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth margin="normal" label="Atividade do cronograma (opcional)" value={form.activity_id}
                        disabled={!form.workstream_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })}>
                        <MenuItem value="">Sem atividade</MenuItem>
                        {(formProject?.workstreams.find((w) => String(w.id) === form.workstream_id)?.activities || []).map((a) => (
                            <MenuItem key={a.id} value={String(a.id)}>{'\u00a0\u00a0'.repeat(a.depth * 2)}{a.name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField fullWidth required margin="normal" label="Título" value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <TextField fullWidth multiline minRows={2} margin="normal" label="Descrição" value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <Stack direction="row" spacing={2}>
                        <TextField fullWidth type="date" margin="normal" label="Prazo" InputLabelProps={{ shrink: true }} value={form.due_date}
                            onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                        <TextField select fullWidth margin="normal" label="Prioridade" value={form.priority}
                            onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}>
                            {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((k) => <MenuItem key={k} value={k}>{TASK_PRIORITY_LABELS[k]}</MenuItem>)}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={saveForm} disabled={!form.project_id || !form.title.trim()}>Salvar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!commenting} onClose={() => setCommenting(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Comentário — {commenting?.title}</DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline minRows={3} margin="normal" value={comment} autoFocus
                        label="O que avançou / o que está impedindo" onChange={(e) => setComment(e.target.value)}
                        helperText="Fica registrado na tarefa com a data de hoje e o gestor vê no quadro do projeto" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommenting(null)}>Cancelar</Button>
                    <Button variant="contained" disabled={!comment.trim()}
                        onClick={async () => { if (commenting && await update(commenting, { comment })) setCommenting(null); }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyTasksSection;
